/* Small progressive enhancements; no animation libraries or scroll hijacking. */
(() => {
  'use strict';
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reducedMotion = () => motionQuery.matches || document.body.hasAttribute('data-reduce-animations');
  document.documentElement?.classList.add('al-smooth-scroll');
  const savesData = () => Boolean(navigator.connection && navigator.connection.saveData);

  class AllisonsVideo extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.video = this.querySelector('video');
      this.toggle = this.querySelector('[data-video-toggle]');
      if (!this.video || !this.toggle) return;
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      const bounds = this.getBoundingClientRect();
      this.inView = bounds.bottom > 0 && bounds.top < window.innerHeight;
      this.userPaused = false;
      this.userPlayed = false;
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.playsInline = true;
      this.video.controls = false;
      this.video.autoplay = this.dataset.autoplay === 'true' && !motionQuery.matches && !savesData();
      this.toggle.hidden = false;
      this.toggle.addEventListener('click', () => {
        if (this.video.paused) {
          this.userPaused = false;
          this.userPlayed = true;
          this.playVideo();
        } else {
          this.userPaused = true;
          this.userPlayed = false;
          this.video.pause();
        }
      }, options);
      this.video.addEventListener('playing', () => {
        this.classList.add('is-video-ready');
        this.updateControl(true);
      }, options);
      this.video.addEventListener('pause', () => this.updateControl(false), options);
      this.video.addEventListener('error', () => {
        this.classList.remove('is-video-ready');
        this.updateControl(false);
      }, options);
      this.video.addEventListener('canplay', () => this.syncPlayback(), options);
      document.addEventListener('visibilitychange', () => this.syncPlayback(), options);
      window.addEventListener('pageshow', () => this.syncPlayback(), options);
      motionQuery.addEventListener('change', () => {
        this.userPlayed = false;
        this.syncPlayback();
      }, options);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.inView = entries[0].isIntersecting;
          this.syncPlayback();
        }, { threshold: 0.08 });
        this.observer.observe(this);
      } else {
        this.inView = true;
        this.syncPlayback();
      }
      this.updateControl(!this.video.paused);
      this.syncPlayback();
    }

    loadVideo() {
      if (this.video.dataset.loaded) return;
      const pendingSources = this.video.querySelectorAll('source[data-src]');
      pendingSources.forEach(source => {
        source.src = source.dataset.src;
      });
      this.video.dataset.loaded = 'true';
      if (pendingSources.length) this.video.load();
    }

    playVideo() {
      this.loadVideo();
      if (!this.video.paused) return;
      const attempt = this.video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(() => this.updateControl(false));
      }
    }

    syncPlayback() {
      const mayAutoplay = this.dataset.autoplay === 'true' && !motionQuery.matches && !savesData();
      if (this.inView && !document.hidden && !this.userPaused && (this.userPlayed || mayAutoplay)) {
        this.playVideo();
      } else {
        this.video.pause();
      }
    }

    updateControl(playing) {
      this.toggle.setAttribute('aria-label', playing ? 'Pause background video' : 'Play background video');
      this.toggle.querySelector('[data-video-label]').textContent = playing ? 'Pause video' : 'Play video';
      this.toggle.querySelector('[data-video-play]').hidden = playing;
      this.toggle.querySelector('[data-video-pause]').hidden = !playing;
    }

    disconnectedCallback() {
      if (this.video) this.video.pause();
      if (this.observer) this.observer.disconnect();
      if (this.abortController) this.abortController.abort();
      this.abortController = null;
    }
  }

  if (!customElements.get('allisons-video')) {
    customElements.define('allisons-video', AllisonsVideo);
  }


  class AllisonsAreaMap extends HTMLElement {
    connectedCallback() {
      if (this.mapAbort) return;
      this.mapAbort = new AbortController();
      const options = { signal: this.mapAbort.signal };
      const activate = key => {
        this.querySelectorAll('[data-area-key]').forEach(item => item.classList.toggle('is-active', item.dataset.areaKey === key));
        this.querySelectorAll('[data-area-line]').forEach(line => line.classList.toggle('is-active', line.dataset.areaLine === key));
      };
      this.addEventListener('pointerover', event => {
        const item = event.target.closest('[data-area-key]');
        if (item) activate(item.dataset.areaKey);
      }, options);
      this.addEventListener('pointerleave', () => activate(this.querySelector(':focus')?.dataset.areaKey), options);
      this.addEventListener('focusin', event => {
        const item = event.target.closest('[data-area-key]');
        if (item) activate(item.dataset.areaKey);
      }, options);
      this.addEventListener('focusout', event => {
        const item = event.relatedTarget?.closest('[data-area-key]');
        activate(item && this.contains(item) ? item.dataset.areaKey : null);
      }, options);
    }
    disconnectedCallback() {
      this.mapAbort?.abort();
      this.mapAbort = null;
    }
  }
  if (!customElements.get('allisons-area-map')) customElements.define('allisons-area-map', AllisonsAreaMap);

  let revealObserver;
  if ('IntersectionObserver' in window) {
    revealObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        revealObserver.unobserve(entry.target);
      });
    }, { threshold: 0.08 });
  }

  function revealSections(root = document) {
    root.querySelectorAll('.al-category, .al-review, .al-brochure, .al-value, .al-area-row, .al-range, .al-guide__item').forEach((item) => {
      item.setAttribute('data-al-reveal', '');
      const index = Array.from(item.parentElement.children).indexOf(item);
      item.style.transitionDelay = `${Math.min(index, 3) * 70}ms`;
    });
    root.querySelectorAll('[data-al-reveal]').forEach(element => {
      if (element.dataset.revealInitialised) return;
      element.dataset.revealInitialised = 'true';
      if (!revealObserver || reducedMotion() || window.Shopify?.designMode) return;
      // Only conceal off-screen content; the initial viewport always renders immediately.
      if (element.getBoundingClientRect().top < window.innerHeight) return;
      element.classList.add('al-reveal-ready');
      revealObserver.observe(element);
    });
  }

  function sizePhotoHeroes() {
    const main = document.querySelector('#site-main');
    if (!main) return;
    const headerHeight = Math.max(0, Math.min(340, main.getBoundingClientRect().top + window.scrollY));
    document.body.style.setProperty('--al-header-height', `${headerHeight}px`);
  }
  sizePhotoHeroes();
  window.addEventListener('resize', sizePhotoHeroes, { passive: true });
  window.addEventListener('load', sizePhotoHeroes);
  document.addEventListener('shopify:section:load', sizePhotoHeroes);

  revealSections();
  document.addEventListener('shopify:section:load', event => revealSections(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (!revealObserver) return;
    event.target.querySelectorAll('[data-al-reveal]').forEach(element => revealObserver.unobserve(element));
  });
  motionQuery.addEventListener('change', () => {
    if (!reducedMotion()) return;
    document.querySelectorAll('.al-reveal-ready').forEach(element => element.classList.add('is-revealed'));
  });
})();
